import { getAccessToken, getIdentityToken } from '@privy-io/react-auth';

/**
 * Typed HTTP client for the journal API.
 *
 * - Attaches the current Privy access token (Privy refreshes it when needed)
 *   plus the identity token so the server can read the user's email.
 * - Every request has a timeout so the UI never hangs on a dead connection.
 * - Idempotent GETs are retried once on network failure / 5xx.
 * - Errors are surfaced as `ApiError` with the server's `code`, `details`
 *   and `requestId`, so components can show precise messages and support
 *   can correlate a screenshot with a server log line.
 * - A 401 from the API signs the user out (the session is gone or revoked).
 */

const API_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
const DEFAULT_TIMEOUT_MS = 30_000;
const LONG_TIMEOUT_MS = 180_000; // imports / broker provisioning

export interface ApiErrorBody {
  error?: string;
  code?: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error || fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code || (status === 0 ? 'NETWORK_ERROR' : 'HTTP_ERROR');
    this.details = body?.details;
    this.requestId = body?.requestId;
  }

  get isNetworkError() {
    return this.status === 0;
  }
  get isValidationError() {
    return this.status === 400 && this.code === 'VALIDATION_ERROR';
  }
}

export interface RequestOptions {
  /** Override the default timeout (ms). */
  timeoutMs?: number;
  /** Abort signal from the caller (e.g. React Query). */
  signal?: AbortSignal;
  /** Retry idempotent requests on transient failures. Default: GET only. */
  retry?: boolean;
  headers?: Record<string, string>;
}

let onUnauthorized: (() => void) | null = null;
/** Register what should happen when the API rejects our session (set by the auth store). */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

/**
 * Auth headers for the current session. `getAccessToken` refreshes the token
 * when it is close to expiry; both calls return null when logged out. They
 * require `PrivyProvider` to have rendered, which is always true in this app.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  try {
    const [accessToken, identityToken] = await Promise.all([getAccessToken(), getIdentityToken().catch(() => null)]);
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    if (identityToken) headers['privy-id-token'] = identityToken;
  } catch {
    // Not logged in (or Privy not ready) → send the request unauthenticated; the server answers 401.
  }
  return headers;
}

function isLongRunning(path: string) {
  return path.includes('/import') || path.includes('/ai/') || path.includes('/broker-connections/connect') || path.includes('/broker-connections/test') || /\/broker-connections\/[^/]+\/sync/.test(path);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function request<T>(method: string, path: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
  const timeoutMs = options.timeoutMs ?? (isLongRunning(path) ? LONG_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);
  const shouldRetry = options.retry ?? method === 'GET';
  const maxAttempts = shouldRetry ? 2 : 1;

  let lastError: ApiError | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    if (options.signal) {
      if (options.signal.aborted) controller.abort();
      else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    try {
      const res = await fetch(`${API_URL}${path}`, {
        method,
        headers: {
          ...(data !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(await authHeaders()),
          ...options.headers,
        },
        body: data === undefined ? undefined : JSON.stringify(data),
        signal: controller.signal,
      });

      if (res.ok) {
        if (res.status === 204) return undefined as T;
        const text = await res.text();
        return (text ? JSON.parse(text) : undefined) as T;
      }

      const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
      const err = new ApiError(res.status, body, `Request failed (${res.status})`);
      if (res.status === 401) {
        onUnauthorized?.();
        throw err;
      }
      if (res.status >= 500 && attempt < maxAttempts) {
        lastError = err;
      } else {
        throw err;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (attempt === maxAttempts) throw err;
        lastError = err;
      } else if (options.signal?.aborted) {
        throw new ApiError(0, { error: 'Request cancelled', code: 'CANCELLED' }, 'Request cancelled');
      } else {
        const aborted = err instanceof DOMException && err.name === 'AbortError';
        lastError = new ApiError(0, { error: aborted ? 'The request timed out. Please try again.' : 'Cannot reach the server. Check your connection.', code: aborted ? 'TIMEOUT' : 'NETWORK_ERROR' }, 'Network error');
        if (attempt === maxAttempts) throw lastError;
      }
    } finally {
      clearTimeout(timer);
    }
    await sleep(300 * attempt);
  }
  throw lastError ?? new ApiError(0, null, 'Request failed');
}

// `T` defaults to `any` so existing untyped call sites keep compiling; pass an
// explicit type parameter (`api.get<Trade[]>(...)`) to opt into type safety.
/* eslint-disable @typescript-eslint/no-explicit-any */
export const api = {
  get: <T = any>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T = any>(path: string, data: unknown, options?: RequestOptions) => request<T>('POST', path, data ?? {}, options),
  put: <T = any>(path: string, data: unknown, options?: RequestOptions) => request<T>('PUT', path, data ?? {}, options),
  patch: <T = any>(path: string, data: unknown, options?: RequestOptions) => request<T>('PATCH', path, data ?? {}, options),
  delete: <T = any>(path: string, options?: RequestOptions) => request<T>('DELETE', path, undefined, options),
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Human-readable message for any thrown value (ApiError, Error, unknown). */
export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof ApiError) {
    if (err.isValidationError && Array.isArray(err.details) && err.details.length) {
      const first = err.details[0] as { path?: string; message?: string };
      return first.path ? `${first.path}: ${first.message}` : first.message || err.message;
    }
    return err.message;
  }
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
